/**
 * ReporteEmbarquesPage — cuánta gente subió al bote entre dos fechas.
 *
 * Es distinto de "Pasajeros por tour", y la diferencia es el punto del
 * reporte: aquel mide lo PROGRAMADO (reservas cuya salida ya pasó), éste mide
 * el HECHO (quién pasó por el check-in del muelle, a qué hora y en qué bote).
 *
 * Y cuenta las cortesías. Son personas que embarcan sin pagar: para la plata
 * no cuentan, para la capacidad del bote y para la autoridad marítima sí. Por
 * eso la pantalla nunca muestra un solo número de "pax": muestra `personas`
 * (todas) y `pax pagos` (los que compraron) uno al lado del otro, con las
 * cortesías visibles en el medio. Si se fundieran en una cifra volveríamos al
 * problema que este reporte existe para resolver.
 *
 * Alcance: PASADÍAS. Los charters se registran pero todavía no se operan a
 * fondo, así que no entran — mezclarlos pondría dos negocios distintos en la
 * misma cifra. El backend los sabe contar y el parámetro sigue ahí para el día
 * que se usen.
 *
 * Backend:
 *   GET /turismo/reportes/embarques        → JSON
 *   GET /turismo/reportes/embarques.xlsx   → Excel (detalle + por bote + por pasadía)
 *   GET /turismo/reportes/embarques.pdf    → PDF apaisado
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Stack, Chip, Alert,
  LinearProgress, Tooltip, Collapse, IconButton,
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { API_BASE, getEmpresaId, getAuthHeaders } from '../booking/shared/format';

const fmt = (n) => (n || 0).toLocaleString('es-CO');

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const firstDayOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const restarDias = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// El día viene como 'YYYY-MM-DD' (día calendario ya resuelto en la zona de la
// empresa por el backend). Partirlo a mano evita que `new Date()` lo lea como
// UTC y lo corra un día hacia atrás.
const fmtDia = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-CO',
    { weekday: 'short', day: '2-digit', month: 'short' });
};

// Tope de filas que se pintan en pantalla. Más que esto no se lee: para eso
// está la descarga, que no tiene límite.
const MAX_FILAS_PANTALLA = 300;

export default function ReporteEmbarquesPage() {
  const empresaId = getEmpresaId();

  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonthISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [paqueteId, setPaqueteId] = useState('');
  const [embarcacionId, setEmbarcacionId] = useState('');
  const [verDetalle, setVerDetalle] = useState(false);

  const [paquetes, setPaquetes] = useState([]);
  const [embarcaciones, setEmbarcaciones] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!empresaId) return;
    fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(l => setPaquetes(Array.isArray(l) ? l : []))
      .catch(() => setPaquetes([]));
    fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(l => setEmbarcaciones(Array.isArray(l) ? l : []))
      .catch(() => setEmbarcaciones([]));
  }, [empresaId]);

  const qsFiltros = useCallback((extra = {}) => {
    const qs = new URLSearchParams({
      empresa_id: empresaId,
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      // El reporte es de pasadías. Explícito en la URL para que un enlace
      // guardado no cambie de alcance si mañana cambia el default del backend.
      incluir_charters: 'false',
      ...extra,
    });
    if (paqueteId) qs.append('paquete_id', paqueteId);
    if (embarcacionId) qs.append('embarcacion_id', embarcacionId);
    return qs;
  }, [empresaId, fechaDesde, fechaHasta, paqueteId, embarcacionId]);

  const cargar = useCallback(async (conDetalle) => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = qsFiltros({ incluir_detalle: String(!!conDetalle) });
      const r = await fetch(`${API_BASE}/turismo/reportes/embarques?${qs}`,
                            { headers: getAuthHeaders() });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId, qsFiltros]);

  useEffect(() => { cargar(false); }, []);  // eslint-disable-line

  const abrirDetalle = () => {
    const nuevo = !verDetalle;
    setVerDetalle(nuevo);
    // El detalle se pide sólo cuando alguien lo abre: en un rango largo son
    // miles de filas que nadie estaba mirando.
    if (nuevo && !(data?.filas || []).length) cargar(true);
  };

  const descargar = (ext) =>
    window.open(`${API_BASE}/turismo/reportes/embarques.${ext}?${qsFiltros()}`, '_blank');

  const emb = data?.embarcados;
  const ns = data?.no_show;
  const filas = data?.filas || [];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* ── Encabezado ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          background: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', boxShadow: '0 6px 16px rgba(14,116,144,0.4)', flexShrink: 0,
        }}>
          <DirectionsBoatIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
            Embarques por rango
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Quién subió al bote entre dos fechas · por el registro de check-in del
            muelle, no por la fecha programada de la salida. Solo pasadías.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<TableChartIcon />}
            onClick={() => descargar('xlsx')} disabled={loading || !data}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Excel
          </Button>
          <Button variant="contained" startIcon={<PictureAsPdfIcon />}
            onClick={() => descargar('pdf')} disabled={loading || !data}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#0e7490',
                  '&:hover': { bgcolor: '#155e75' } }}>
            PDF
          </Button>
        </Stack>
      </Box>

      {/* ── Filtros ── */}
      <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          {[['Este mes', firstDayOfMonthISO()], ['Últimos 7 días', restarDias(7)],
            ['Últimos 30 días', restarDias(30)], ['Últimos 90 días', restarDias(90)],
          ].map(([label, desde]) => (
            <Chip key={label} label={label} size="small" variant="outlined"
              onClick={() => { setFechaDesde(desde); setFechaHasta(todayISO()); }}
              sx={{ fontWeight: 600, fontSize: 11.5 }} />
          ))}
        </Stack>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" type="date" label="Desde" value={fechaDesde}
            onChange={e => setFechaDesde(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 155 }} />
          <TextField size="small" type="date" label="Hasta" value={fechaHasta}
            onChange={e => setFechaHasta(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 155 }} />
          <TextField size="small" select label="Pasadía" value={paqueteId}
            onChange={e => setPaqueteId(e.target.value)} sx={{ minWidth: 190 }}>
            <MenuItem value="">Todas</MenuItem>
            {paquetes.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Bote" value={embarcacionId}
            onChange={e => setEmbarcacionId(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">Todos</MenuItem>
            {embarcaciones.map(b => <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>)}
          </TextField>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
            onClick={() => cargar(verDetalle)} disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Aplicar
          </Button>
          {data && (
            <Chip size="small" label={`${data.dias_rango} día${data.dias_rango !== 1 ? 's' : ''}`}
              sx={{ bgcolor: '#ecfeff', color: '#155e75', fontWeight: 700, fontSize: 11 }} />
          )}
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>{error}</Alert>}

      {!loading && data && (
        <>
          {/* ── KPIs ── */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 1.5, mb: 1,
          }}>
            <Kpi label="Personas embarcadas" value={fmt(emb.personas)} color="#0e7490"
                 sub="todo el que subió al bote" destacado />
            <Kpi label="Pax pagos" value={fmt(emb.pax_pagos)} color="#0891b2"
                 sub={`${fmt(emb.adultos)} ad · ${fmt(emb.ninos)} ni · ${fmt(emb.bebes)} be`} />
            <Kpi label="Cortesías" value={fmt(emb.cortesias)} color="#7c3aed"
                 sub="embarcan sin pagar" />
            <Kpi label="Reservas" value={fmt(emb.reservas)} color="#0f766e"
                 sub="con embarque registrado" />
            <Kpi label="No-show" value={fmt(ns.personas)} color="#b91c1c"
                 sub={`${fmt(ns.reservas)} reservas · ${data.tasa_no_show_pct}% de ausencia`} />
          </Box>

          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: 2.5,
                     p: 1.25, bgcolor: '#ecfeff', border: '1px solid #a5f3fc',
                     borderRadius: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#155e75', mt: '1px' }} />
            <Typography sx={{ fontSize: 12, color: '#155e75' }}>
              <strong>Personas</strong> es la cifra de capacidad y la que pide la
              autoridad marítima: incluye las cortesías. <strong>Pax pagos</strong> es
              la cifra contable. Van separadas a propósito — el anexo de ventas usa
              la segunda y por eso sus totales son menores que los de acá.
            </Typography>
          </Box>

          {data.embarques_sin_sello_hora > 0 && (
            <Alert severity="info" sx={{ mb: 2, fontSize: 12.5 }}>
              {data.embarques_sin_sello_hora} embarque(s) del rango no tienen hora de
              check-in registrada. Se cuentan por la fecha de su salida.
            </Alert>
          )}

          {/* ── Por bote y por pasadía ── */}
          <Box sx={{ display: 'grid',
                     gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                     gap: 2, mb: 2 }}>
            <Panel titulo="Por embarcación"
                   ayuda="Cuántas personas llevó cada bote en el rango. Es la vista de capacidad.">
              <TablaGrupos grupos={data.por_bote} etiqueta="Embarcación" />
            </Panel>
            <Panel titulo="Por pasadía"
                   ayuda="Qué producto movió más gente en el rango.">
              <TablaGrupos grupos={data.por_paquete} etiqueta="Pasadía" />
            </Panel>
          </Box>

          {/* ── Por día ── */}
          <Panel titulo="Por día" ayuda="Cada día del rango con movimiento registrado.">
            <TablaDias dias={data.por_dia} />
          </Panel>

          {/* ── Detalle ── */}
          <Box sx={{ mt: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Box onClick={abrirDetalle}
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1,
                    cursor: 'pointer', userSelect: 'none' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', flex: 1 }}>
                Detalle por reserva
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                {verDetalle ? 'Ocultar' : 'Ver'}
              </Typography>
              <IconButton size="small">
                {verDetalle ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={verDetalle}>
              <Box sx={{ px: 2, pb: 2 }}>
                {filas.length > MAX_FILAS_PANTALLA && (
                  <Alert severity="info" sx={{ mb: 1.5, fontSize: 12 }}>
                    Se muestran las primeras {MAX_FILAS_PANTALLA} de {fmt(filas.length)} filas.
                    El Excel y el PDF las traen todas.
                  </Alert>
                )}
                <TablaDetalle filas={filas.slice(0, MAX_FILAS_PANTALLA)} />
              </Box>
            </Collapse>
          </Box>
        </>
      )}
    </Box>
  );
}


function Kpi({ label, value, sub, color, destacado }) {
  return (
    <Box sx={{
      p: 1.75, borderRadius: 2, minHeight: 92,
      bgcolor: destacado ? color : '#fff',
      color: destacado ? '#fff' : '#0f172a',
      border: destacado ? 'none' : '1px solid #e2e8f0',
      boxShadow: destacado ? '0 4px 14px rgba(14,116,144,0.3)' : 'none',
    }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.1,
                        color: destacado ? 'rgba(255,255,255,0.85)' : '#64748b',
                        textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, mt: 0.25, lineHeight: 1.15,
                        color: destacado ? '#fff' : color }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 10.5, mt: 0.25,
                        color: destacado ? 'rgba(255,255,255,0.75)' : '#64748b' }}>
        {sub}
      </Typography>
    </Box>
  );
}

function Panel({ titulo, ayuda, children }) {
  return (
    <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
          {titulo}
        </Typography>
        {ayuda && (
          <Tooltip title={ayuda}>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  );
}

const thSx = {
  fontSize: 10.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase',
  letterSpacing: 0.4, textAlign: 'right', padding: '6px 8px',
  borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
};
const tdSx = {
  fontSize: 12.5, color: '#0f172a', textAlign: 'right', padding: '6px 8px',
  borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap',
};

function TablaGrupos({ grupos, etiqueta }) {
  if (!grupos?.length) {
    return <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
      Sin movimiento en el rango.
    </Typography>;
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thSx, textAlign: 'left' }}>{etiqueta}</th>
            <th style={thSx}>Pax pagos</th>
            <th style={thSx}>Cortesías</th>
            <th style={thSx}>Personas</th>
            <th style={thSx}>No-show</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map(g => (
            <tr key={String(g.clave)}>
              <td style={{ ...tdSx, textAlign: 'left', fontWeight: 600 }}>
                {g.nombre || '—'}
              </td>
              <td style={tdSx}>{fmt(g.embarcados.pax_pagos)}</td>
              <td style={{ ...tdSx, color: g.embarcados.cortesias ? '#7c3aed' : '#cbd5e1' }}>
                {fmt(g.embarcados.cortesias)}
              </td>
              <td style={{ ...tdSx, fontWeight: 800, color: '#0e7490' }}>
                {fmt(g.embarcados.personas)}
              </td>
              <td style={{ ...tdSx, color: g.no_show.personas ? '#b91c1c' : '#cbd5e1' }}>
                {fmt(g.no_show.personas)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function TablaDias({ dias }) {
  if (!dias?.length) {
    return <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
      Sin movimiento en el rango.
    </Typography>;
  }
  const max = Math.max(...dias.map(d => d.embarcados.personas), 1);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thSx, textAlign: 'left' }}>Día</th>
            <th style={{ ...thSx, textAlign: 'left', width: '38%' }}>Personas</th>
            <th style={thSx}>Pax pagos</th>
            <th style={thSx}>Cortesías</th>
            <th style={thSx}>Reservas</th>
            <th style={thSx}>No-show</th>
          </tr>
        </thead>
        <tbody>
          {dias.map(d => (
            <tr key={d.dia}>
              <td style={{ ...tdSx, textAlign: 'left', fontWeight: 600 }}>
                {fmtDia(d.dia)}
              </td>
              <td style={{ ...tdSx, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1, height: 8, bgcolor: '#e2e8f0', borderRadius: 4,
                             overflow: 'hidden', minWidth: 60 }}>
                    <Box sx={{ height: '100%', bgcolor: '#0e7490',
                               width: `${(d.embarcados.personas / max) * 100}%` }} />
                  </Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0e7490',
                                    minWidth: 32, textAlign: 'right' }}>
                    {fmt(d.embarcados.personas)}
                  </Typography>
                </Box>
              </td>
              <td style={tdSx}>{fmt(d.embarcados.pax_pagos)}</td>
              <td style={{ ...tdSx, color: d.embarcados.cortesias ? '#7c3aed' : '#cbd5e1' }}>
                {fmt(d.embarcados.cortesias)}
              </td>
              <td style={tdSx}>{fmt(d.reservas_embarcadas)}</td>
              <td style={{ ...tdSx, color: d.no_show.personas ? '#b91c1c' : '#cbd5e1' }}>
                {fmt(d.no_show.personas)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function TablaDetalle({ filas }) {
  if (!filas?.length) {
    return <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
      Sin embarques en el rango con los filtros aplicados.
    </Typography>;
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thSx, textAlign: 'left' }}>Día</th>
            <th style={{ ...thSx, textAlign: 'left' }}>Hora</th>
            <th style={{ ...thSx, textAlign: 'left' }}>Código</th>
            <th style={{ ...thSx, textAlign: 'left' }}>Titular</th>
            <th style={{ ...thSx, textAlign: 'left' }}>Pasadía</th>
            <th style={{ ...thSx, textAlign: 'left' }}>Bote</th>
            <th style={thSx}>Pax pagos</th>
            <th style={thSx}>Cortesías</th>
            <th style={thSx}>Personas</th>
          </tr>
        </thead>
        <tbody>
          {filas.map(f => (
            <tr key={f.reservacion_id}
                style={{ background: f.embarco ? 'transparent' : '#fef2f2' }}>
              <td style={{ ...tdSx, textAlign: 'left' }}>{fmtDia(f.dia)}</td>
              <td style={{ ...tdSx, textAlign: 'left' }}>
                {f.embarco ? (f.hora_embarque || '—')
                  : <Chip label="No-show" size="small"
                      sx={{ height: 18, fontSize: 10, fontWeight: 700,
                            bgcolor: '#fee2e2', color: '#991b1b' }} />}
              </td>
              <td style={{ ...tdSx, textAlign: 'left', fontFamily: 'monospace',
                           fontSize: 11.5 }}>
                {f.codigo_reserva}
              </td>
              <td style={{ ...tdSx, textAlign: 'left' }}>{f.nombre_cliente}</td>
              <td style={{ ...tdSx, textAlign: 'left' }}>{f.paquete_nombre}</td>
              <td style={{ ...tdSx, textAlign: 'left' }}>{f.embarcacion_nombre || '—'}</td>
              <td style={tdSx}>{fmt(f.pax_pagos)}</td>
              <td style={{ ...tdSx, color: f.cortesias ? '#7c3aed' : '#cbd5e1' }}>
                {fmt(f.cortesias)}
              </td>
              <td style={{ ...tdSx, fontWeight: 800, color: f.embarco ? '#0e7490' : '#b91c1c' }}>
                {fmt(f.personas)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}
